import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import ConnectGoogleAdsButton from '@/components/ConnectGoogleAdsButton';
import { AdsAccountPickerDialog } from '@/components/ads/AdsAccountPickerDialog';
import { useAdsAccounts, useAdsAccountActions, useAdsRolloutAccountIds, type AdsAccount } from '@/hooks/useAdsPerformance';
import { useAdsConnectFlow } from '@/hooks/useAdsConnectFlow';
import { maskCustomerId } from '@/lib/maskCustomerId';

export interface GoogleAdsAccountRowProps {
  googleAccountId: string;
  /** Die Merchant Center GENAU DIESES Google-Kontos, in der Reihenfolge der Seite. */
  merchantCenters: Array<{ id: string; name: string | null }>;
}

/**
 * Zeile "Google Ads" in der Konto-Karte von Settings > Accounts (Spec 4.4).
 *
 * Das Ads-Konto haengt an genau diesem Google-Konto
 * (`google_ads_accounts.google_account_id`); gezeigt werden deshalb nur dessen
 * Zeilen. Zugeordnet wird ueber denselben Ablauf wie im Feed-Check
 * (`useAdsConnectFlow` -> `link_google_ads_account`), getrennt ueber
 * `disconnect_google_ads_account`. Die Verknuepfung
 * (`linked_merchant_center_id`) bleibt beim Trennen stehen, das ist Absicht:
 * die RPC nullt die Tokens, und wer spaeter neu verbindet, findet seine
 * Zuordnung wieder vor.
 *
 * DREI ENTSCHEIDUNGEN, DIE HIER ANDERS SIND ALS IM FEED-CHECK:
 *
 * 1. Zugeordnet wird NUR, wenn die Karte GENAU EIN Merchant Center fuehrt, und
 *    die Zeile nennt dieses Ziel im Klartext. Grund: `link_google_ads_account`
 *    verweigert jedes spaetere Umhaengen einer bereits zugeordneten Zeile, und
 *    einen Loese-Pfad fuer `linked_merchant_center_id` gibt es im Client nicht
 *    -- ein Griff auf die falsche MC waere dauerhaft. Bei null oder mehreren
 *    Merchant Centern verweist die Zeile auf den Feed-Check des jeweiligen
 *    Merchant Centers, wo die Auswahl ohnehin schon getroffen ist. Die aktive
 *    MC des `SelectionContext` ist hier nie das Ziel: sie kann einem ANDEREN
 *    Google-Konto gehoeren.
 * 2. Der Verbinden-Knopf steht IMMER da, auch mit vorhandenen Zeilen (Muster
 *    `AdsPerformance.tsx`). `disconnect_google_ads_account` loescht die Zeile
 *    nicht, es nullt die Token-Spalten, und die kann der Client nicht lesen.
 *    Haengt der Knopf an "keine Zeile", gibt es aus dem getrennten Zustand
 *    keinen Rueckweg, und die Karte behauptet danach "Verbunden" fuer ein
 *    Konto, das der naechtliche Pull nicht lesen kann.
 * 3. `urlReturnMode: 'notify-only'`. Der Vollbild-Rueckweg wird modulweit von
 *    genau einer Instanz verbraucht, und das ist die zuerst gerenderte Karte,
 *    nicht die, deren Konto den Consent gegeben hat. Sie meldet deshalb nur;
 *    zugeordnet wird per Klick auf der richtigen Karte.
 *
 * Der Feed-Kontext fehlt hier (`feedId: null`): ein Erst-Abruf laeuft je Feed,
 * und in den Einstellungen ist keiner gewaehlt. Nach dem Zuordnen steht die
 * Zeile also auf "verbunden", die Zahlen fuellt der naechtliche Lauf oder der
 * naechste Besuch des Feed-Checks.
 *
 * 4. Trennen fragt nach (UX-Audit A6G-04): der Klick nullt die Tokens eines
 *    Kontos, dessen naechtlicher Abruf dann stillsteht. Die Zeilen stehen
 *    nach Name sortiert, nicht in Ladereihenfolge.
 */

/**
 * Feste Reihenfolge der Zeilen (UX-Audit A6G-04). `useAdsAccounts` sortiert
 * nach `connected_at`, und `handleAdsConnect` legt alle Zeilen eines Grants mit
 * demselben Zeitstempel an: die Reihenfolge wechselte zwischen zwei Ladevorgaengen,
 * und "Trennen" sass beim naechsten Blick neben einem anderen Konto.
 * Sortiert wird nach Name, dann Kundennummer, ohne Sprachabhaengigkeit.
 */
export function sortAdsRows(rows: AdsAccount[]): AdsAccount[] {
  const key = (a: AdsAccount) => (a.descriptive_name ?? '').trim().toLowerCase();
  return [...rows].sort((x, y) => {
    const byName = key(x) < key(y) ? -1 : key(x) > key(y) ? 1 : 0;
    if (byName !== 0) return byName;
    return x.ads_customer_id < y.ads_customer_id ? -1 : x.ads_customer_id > y.ads_customer_id ? 1 : 0;
  });
}

export function GoogleAdsAccountRow({ googleAccountId, merchantCenters }: GoogleAdsAccountRowProps) {
  const { t } = useTranslation('optimization');
  const { data } = useAdsAccounts();
  const { disconnectAccount, isDisconnecting } = useAdsAccountActions();
  const rollout = useAdsRolloutAccountIds();
  const rolloutIds: ReadonlySet<string> = rollout.data ?? new Set<string>();

  const rows = ((data ?? []) as AdsAccount[]).filter((a) => a.google_account_id === googleAccountId);
  const hasActive = rows.some((a) => a.is_active);
  // NUR AKTIVE NICHT-MANAGER-ZEILEN BEKOMMEN EINE EIGENE ZEILE (Final-Review
  // F4). `handleAdsConnect` legt bei einem MCC-Grant JEDES `customer_client`
  // -Kind an: google@oma.ag hat live 156 Zeilen, davon 2 Verwaltungskonten und
  // 4 aktive. Die Karte zeigte dort 156 Zeilen mit je einem Trennen-Knopf, und
  // jeder Klick nullte die Tokens einer Zeile, die sie nie als aktiv
  // ausgewiesen hatte. Ein Verwaltungskonto fuehrt selbst keine Anzeigen und
  // ist kein Kandidat fuer irgendetwas.
  const visibleRows = sortAdsRows(rows.filter((a) => a.is_active && !a.is_manager));
  // Alles, was nicht einzeln dasteht, als EINE Zahl. Sie zu verschweigen
  // waere die andere Unwahrheit: der Grant traegt diese Konten, sie sind nur
  // nichts, woran man hier etwas tun kann.
  const hiddenCount = rows.length - visibleRows.length;
  // GENAU EINES, sonst keines: bei mehreren waere die Wahl geraten, und sie
  // ist nicht mehr korrigierbar (siehe Punkt 1 im Kopf).
  const targetMerchantCenter = merchantCenters.length === 1 ? merchantCenters[0] : null;
  const canAssign = rows.length > 0 && !hasActive && targetMerchantCenter !== null;

  const flow = useAdsConnectFlow({
    source: 'settings',
    urlReturnMode: 'notify-only',
    merchantCenterId: targetMerchantCenter?.id ?? null,
    googleAccountId,
    feedId: null,
  });

  const mcName = (id: string | null) => merchantCenters.find((m) => m.id === id)?.name ?? null;

  const handleDisconnect = async (adsAccountId: string) => {
    try {
      await disconnectAccount(adsAccountId);
    } catch {
      // `disconnectAccount` ist ein `mutateAsync` und WIRFT bei einem `false`
      // der RPC. Ohne diesen Fang bliebe der Fehlschlag eine unbehandelte
      // Rejection in der Konsole und fuer den Nutzer vollkommen stumm.
      toast.error(t('adsPerformance:errors.disconnectFailed'));
    }
  };

  return (
    <div data-testid={`ads-row-${googleAccountId}`} className="flex flex-col gap-3 border-t border-border/40 pt-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{t('feedCheck.ads.settings.label')}</span>
        <Badge variant={hasActive ? 'default' : 'outline'}>
          {rows.length === 0
            ? t('feedCheck.ads.settings.notConnected')
            : hasActive
              ? t('feedCheck.ads.settings.connected')
              // Ein Abzeichen beschreibt einen ZUSTAND. "Konto zuordnen" stand
              // hier als Aufforderung und noch einmal auf dem Knopf darunter.
              : t('feedCheck.ads.settings.notAssigned')}
        </Badge>
      </div>

      {/* Auch nicht ZUGEORDNETE Zeilen stehen hier mit Kundennummer und
          Trennen-Knopf, solange sie aktiv sind: sie sind verbunden (Tokens
          liegen bei uns), und ein Konto, das man verbunden hat, muss man auch
          ohne Zuordnung wieder loesen koennen. Inaktive und Verwaltungskonten
          stehen dagegen nur noch in der Sammelzeile darunter (F4). */}
      {visibleRows.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-2 text-xs">
          <span className="min-w-0 truncate">
            {maskCustomerId(a.ads_customer_id)}{a.descriptive_name ? ` · ${a.descriptive_name}` : ''}
            <span className="text-muted-foreground">
              {' · '}
              {/* Der Zuordnungs-Satz steht in einem EIGENEN Element: mit dem
                  Trennzeichen im selben Textknoten waere er nur noch als
                  Teilstring auffindbar, fuer den Test wie fuer einen
                  Screenreader, der die Zeile stueckweise vorliest. */}
              <span>
                {a.linked_merchant_center_id
                  ? t('feedCheck.ads.settings.linkedTo', { mc: mcName(a.linked_merchant_center_id) ?? maskCustomerId(a.ads_customer_id) })
                  : t('feedCheck.ads.settings.unassigned')}
              </span>
            </span>
          </span>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                data-testid={`ads-disconnect-${a.id}`}
                variant="ghost"
                size="sm"
                className="ml-3 h-7 shrink-0 px-2 text-xs text-destructive-ink"
                disabled={isDisconnecting}
              >
                {t('feedCheck.ads.settings.disconnect')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('feedCheck.ads.settings.disconnectTitle', {
                    account: a.descriptive_name ?? maskCustomerId(a.ads_customer_id),
                  })}
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <span className="block space-y-2 text-sm text-muted-foreground">
                    <span className="block">{t('feedCheck.ads.settings.disconnectBody')}</span>
                    {rolloutIds.has(a.id) && (
                      <span data-testid={`ads-disconnect-rollout-${a.id}`} className="block font-medium text-foreground">
                        {t('feedCheck.ads.settings.disconnectRollout')}
                      </span>
                    )}
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('feedCheck.ads.settings.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`ads-disconnect-confirm-${a.id}`}
                  onClick={() => { void handleDisconnect(a.id); }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t('feedCheck.ads.settings.disconnectConfirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ))}

      {hiddenCount > 0 && (
        <p data-testid="ads-other-accounts" className="text-[11px] text-muted-foreground">
          {t('feedCheck.ads.settings.otherAccounts', { count: hiddenCount })}
        </p>
      )}

      {/* Immer sichtbar, siehe Punkt 2 im Kopf. Mit vorhandenen Zeilen traegt
          er seine eigene Beschriftung: "verbinden" waere daneben die falsche
          Aussage, gemeint ist der Rueckweg aus dem getrennten Zustand. */}
      <ConnectGoogleAdsButton
        returnTo="settings"
        className="w-full"
        onConnected={flow.onConnected}
        disabled={flow.busy}
        label={rows.length > 0 ? t('feedCheck.ads.settings.reconnect') : undefined}
      />

      {/* "Konto zuordnen" NUR ohne aktive Zeile UND mit genau einem Ziel: gibt
          es noch gar keine Zeile, stuende der Dialog leer da; mit einer aktiven
          ist nichts offen; ohne eindeutiges Ziel waere die Zuordnung geraten. */}
      {canAssign && targetMerchantCenter && (
        <div className="flex flex-col gap-1">
          <Button data-testid="ads-assign-settings" variant="outline" size="sm" className="w-full" disabled={flow.busy} onClick={flow.openPicker}>
            {t('feedCheck.ads.settings.assign')}
          </Button>
          {/* Das Ziel steht vor dem Klick da, nicht erst danach: umhaengen
              laesst es sich hinterher nicht mehr. */}
          <span className="text-[11px] text-muted-foreground">
            {t('feedCheck.ads.settings.assignTarget', { mc: targetMerchantCenter.name ?? targetMerchantCenter.id })}
          </span>
        </div>
      )}

      {rows.length > 0 && !hasActive && !targetMerchantCenter && (
        <p className="text-[11px] text-muted-foreground">{t('feedCheck.ads.settings.assignInFeedCheck')}</p>
      )}

      <AdsAccountPickerDialog
        open={flow.pickerOpen}
        onOpenChange={(o) => (o ? flow.openPicker() : flow.closePicker())}
        candidates={flow.candidates}
        onConfirm={(id) => void flow.confirmLink(id)}
        isLinking={flow.isLinking}
      />
    </div>
  );
}
